import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './UserEngagement.css';

const UserEngagement = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(7);
  const [dashboardData, setDashboardData] = useState(null);
  const [productInterest, setProductInterest] = useState([]);
  const [sellerEngagement, setSellerEngagement] = useState([]);
  const [searchAnalytics, setSearchAnalytics] = useState([]);
  const [funnelData, setFunnelData] = useState(null);
  const [realtimeActions, setRealtimeActions] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAllData();
    // Auto-refresh realtime data every 30 seconds
    const interval = setInterval(() => {
      if (activeTab === 'realtime') {
        fetchRealtimeActions();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [dateRange, activeTab]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [dashboard, products, sellers, searches, funnel, realtime] = await Promise.all([
        axios.get(`/api/user-actions/analytics/dashboard?days=${dateRange}`, config),
        axios.get(`/api/user-actions/analytics/products/interest?days=${dateRange}&limit=10`, config),
        axios.get(`/api/user-actions/analytics/sellers/engagement?days=${dateRange}&limit=10`, config),
        axios.get(`/api/user-actions/analytics/searches/popular?days=${dateRange}&limit=10`, config),
        axios.get(`/api/user-actions/analytics/funnel?days=${dateRange}`, config),
        axios.get(`/api/user-actions/analytics/realtime`, config)
      ]);

      setDashboardData(dashboard.data);
      setProductInterest(products.data.products || []);
      setSellerEngagement(sellers.data.sellers || []);
      setSearchAnalytics(searches.data.searches || []);
      setFunnelData(funnel.data);
      setRealtimeActions(realtime.data.actions || []);
    } catch (error) {
      console.error('Error fetching engagement data:', error);
      toast.error('Failed to load engagement data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRealtimeActions = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`/api/user-actions/analytics/realtime`, config);
      setRealtimeActions(response.data.actions || []);
    } catch (error) {
      console.error('Error fetching realtime actions:', error);
    }
  };

  const exportData = async (format = 'csv') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `/api/user-actions/export?format=${format}&days=${dateRange}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `user-engagement-${dateRange}days.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success(`Data exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];

  if (loading) {
    return (
      <Layout>
        <div className="user-engagement-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading engagement data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="user-engagement-container">
        <div className="engagement-header">
          <div className="header-content">
            <h1>📊 User Engagement Analytics</h1>
            <p>Track user interactions, behavior patterns, and conversion metrics</p>
          </div>
          <div className="header-actions">
            <select 
              value={dateRange} 
              onChange={(e) => setDateRange(Number(e.target.value))}
              className="date-range-selector"
            >
              <option value={1}>Last 24 Hours</option>
              <option value={7}>Last 7 Days</option>
              <option value={30}>Last 30 Days</option>
              <option value={90}>Last 90 Days</option>
            </select>
            <button onClick={() => exportData('csv')} className="export-btn">
              📥 Export CSV
            </button>
            <button onClick={() => exportData('json')} className="export-btn">
              📥 Export JSON
            </button>
            <button onClick={fetchAllData} className="refresh-btn">
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📈 Overview
          </button>
          <button 
            className={`tab-btn ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            🛍️ Products
          </button>
          <button 
            className={`tab-btn ${activeTab === 'sellers' ? 'active' : ''}`}
            onClick={() => setActiveTab('sellers')}
          >
            👥 Sellers
          </button>
          <button 
            className={`tab-btn ${activeTab === 'searches' ? 'active' : ''}`}
            onClick={() => setActiveTab('searches')}
          >
            🔍 Searches
          </button>
          <button 
            className={`tab-btn ${activeTab === 'funnel' ? 'active' : ''}`}
            onClick={() => setActiveTab('funnel')}
          >
            📉 Funnel
          </button>
          <button 
            className={`tab-btn ${activeTab === 'realtime' ? 'active' : ''}`}
            onClick={() => setActiveTab('realtime')}
          >
            ⚡ Real-time
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && dashboardData && (
          <div className="overview-tab">
            {/* Summary Cards */}
            <div className="summary-cards">
              <div className="summary-card blue">
                <div className="card-icon">👥</div>
                <div className="card-content">
                  <h3>{dashboardData.summary?.totalActions?.toLocaleString() || 0}</h3>
                  <p>Total Actions</p>
                  <span className="card-trend">+{dashboardData.summary?.growth || 0}% vs last period</span>
                </div>
              </div>

              <div className="summary-card green">
                <div className="card-icon">🎯</div>
                <div className="card-content">
                  <h3>{dashboardData.summary?.uniqueUsers?.toLocaleString() || 0}</h3>
                  <p>Active Users</p>
                  <span className="card-trend">Engaged users</span>
                </div>
              </div>

              <div className="summary-card orange">
                <div className="card-icon">🛍️</div>
                <div className="card-content">
                  <h3>{dashboardData.summary?.productInteractions?.toLocaleString() || 0}</h3>
                  <p>Product Interactions</p>
                  <span className="card-trend">Views, interests, contacts</span>
                </div>
              </div>

              <div className="summary-card purple">
                <div className="card-icon">💰</div>
                <div className="card-content">
                  <h3>{dashboardData.summary?.conversions?.toLocaleString() || 0}</h3>
                  <p>Conversions</p>
                  <span className="card-trend">{dashboardData.summary?.conversionRate || 0}% rate</span>
                </div>
              </div>
            </div>

            {/* Action Distribution Chart */}
            <div className="chart-container">
              <h2>Action Distribution</h2>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={dashboardData.actionDistribution || []}
                    dataKey="count"
                    nameKey="actionType"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    label
                  >
                    {(dashboardData.actionDistribution || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Peak Activity Times */}
            {dashboardData.peakHours && (
              <div className="chart-container">
                <h2>Peak Activity Hours</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dashboardData.peakHours}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="products-tab">
            <div className="chart-container">
              <h2>🏆 Top Products by Interest</h2>
              <p className="chart-subtitle">Products generating the most user interest</p>
              
              {productInterest.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={productInterest} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="productName" width={150} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="interestCount" fill="#0088FE" name="Interests" />
                      <Bar dataKey="viewCount" fill="#00C49F" name="Views" />
                    </BarChart>
                  </ResponsiveContainer>

                  <div className="data-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Views</th>
                          <th>Interests</th>
                          <th>Conversion Rate</th>
                          <th>Seller</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productInterest.map((product, index) => (
                          <tr key={index}>
                            <td>
                              <strong>{product.productName}</strong>
                              <br />
                              <small>{product.category}</small>
                            </td>
                            <td>{product.viewCount}</td>
                            <td><span className="badge blue">{product.interestCount}</span></td>
                            <td>
                              <div className="progress-bar">
                                <div 
                                  className="progress-fill" 
                                  style={{ width: `${product.conversionRate || 0}%` }}
                                ></div>
                                <span>{product.conversionRate || 0}%</span>
                              </div>
                            </td>
                            <td>{product.sellerName || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <p>No product interaction data available for this period</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sellers Tab */}
        {activeTab === 'sellers' && (
          <div className="sellers-tab">
            <div className="chart-container">
              <h2>🌟 Top Sellers by Engagement</h2>
              <p className="chart-subtitle">Sellers receiving the most user interactions</p>
              
              {sellerEngagement.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={sellerEngagement}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="sellerName" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="phoneClicks" stackId="a" fill="#FF8042" name="Phone" />
                      <Bar dataKey="emailClicks" stackId="a" fill="#FFBB28" name="Email" />
                      <Bar dataKey="whatsappClicks" stackId="a" fill="#00C49F" name="WhatsApp" />
                      <Bar dataKey="chatMessages" stackId="a" fill="#0088FE" name="Chat" />
                    </BarChart>
                  </ResponsiveContainer>

                  <div className="data-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Seller</th>
                          <th>Profile Views</th>
                          <th>📞 Phone</th>
                          <th>📧 Email</th>
                          <th>💬 WhatsApp</th>
                          <th>💭 Chat</th>
                          <th>Total Contacts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sellerEngagement.map((seller, index) => (
                          <tr key={index}>
                            <td><strong>{seller.sellerName}</strong></td>
                            <td>{seller.profileViews}</td>
                            <td><span className="badge orange">{seller.phoneClicks}</span></td>
                            <td><span className="badge yellow">{seller.emailClicks}</span></td>
                            <td><span className="badge green">{seller.whatsappClicks}</span></td>
                            <td><span className="badge blue">{seller.chatMessages}</span></td>
                            <td><strong>{seller.totalContacts}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Contact Method Preferences */}
                  <div className="chart-container">
                    <h2>Contact Method Preferences</h2>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'WhatsApp', value: sellerEngagement.reduce((sum, s) => sum + s.whatsappClicks, 0) },
                            { name: 'Phone', value: sellerEngagement.reduce((sum, s) => sum + s.phoneClicks, 0) },
                            { name: 'Email', value: sellerEngagement.reduce((sum, s) => sum + s.emailClicks, 0) },
                            { name: 'Chat', value: sellerEngagement.reduce((sum, s) => sum + s.chatMessages, 0) }
                          ]}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label
                        >
                          {COLORS.map((color, index) => (
                            <Cell key={`cell-${index}`} fill={color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <p>No seller engagement data available for this period</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Searches Tab */}
        {activeTab === 'searches' && (
          <div className="searches-tab">
            <div className="chart-container">
              <h2>🔍 Popular Search Queries</h2>
              <p className="chart-subtitle">What users are searching for</p>
              
              {searchAnalytics.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={searchAnalytics} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="query" width={150} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" fill="#8884d8" name="Search Count" />
                    </BarChart>
                  </ResponsiveContainer>

                  <div className="data-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Search Query</th>
                          <th>Search Count</th>
                          <th>Avg Results</th>
                          <th>Zero Results</th>
                          <th>Click-through Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchAnalytics.map((search, index) => (
                          <tr key={index}>
                            <td><strong>"{search.query}"</strong></td>
                            <td><span className="badge blue">{search.count}</span></td>
                            <td>{search.avgResults || 0}</td>
                            <td>
                              {search.zeroResultCount > 0 ? (
                                <span className="badge red">{search.zeroResultCount}</span>
                              ) : (
                                <span className="badge green">0</span>
                              )}
                            </td>
                            <td>{search.clickThroughRate || 0}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <p>No search data available for this period</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Funnel Tab */}
        {activeTab === 'funnel' && funnelData && (
          <div className="funnel-tab">
            <div className="chart-container">
              <h2>📉 Conversion Funnel</h2>
              <p className="chart-subtitle">User journey from view to conversion</p>
              
              <div className="funnel-visualization">
                {funnelData.funnel && funnelData.funnel.map((stage, index) => (
                  <div key={index} className="funnel-stage">
                    <div 
                      className="funnel-bar" 
                      style={{ 
                        width: `${stage.percentage}%`,
                        backgroundColor: COLORS[index % COLORS.length]
                      }}
                    >
                      <div className="funnel-content">
                        <span className="funnel-label">{stage.stage}</span>
                        <span className="funnel-count">{stage.count.toLocaleString()}</span>
                        <span className="funnel-percentage">{stage.percentage}%</span>
                      </div>
                    </div>
                    {index < funnelData.funnel.length - 1 && (
                      <div className="funnel-dropoff">
                        <span>↓ {stage.dropoffRate}% drop-off</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="funnel-metrics">
                <div className="metric-card">
                  <h3>Overall Conversion Rate</h3>
                  <div className="metric-value">{funnelData.conversionRate || 0}%</div>
                  <p>From view to conversion</p>
                </div>
                <div className="metric-card">
                  <h3>Average Time to Convert</h3>
                  <div className="metric-value">{funnelData.avgTimeToConvert || 'N/A'}</div>
                  <p>User journey duration</p>
                </div>
                <div className="metric-card">
                  <h3>Biggest Drop-off</h3>
                  <div className="metric-value">{funnelData.biggestDropoff || 'N/A'}</div>
                  <p>Stage with most abandonment</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Real-time Tab */}
        {activeTab === 'realtime' && (
          <div className="realtime-tab">
            <div className="chart-container">
              <h2>⚡ Real-time Activity (Last Hour)</h2>
              <p className="chart-subtitle">Live user actions as they happen</p>
              
              <div className="realtime-stats">
                <div className="stat-box">
                  <span className="stat-number">{realtimeActions.length}</span>
                  <span className="stat-label">Actions in last hour</span>
                </div>
                <div className="stat-box">
                  <span className="stat-number">
                    {new Set(realtimeActions.map(a => a.user)).size}
                  </span>
                  <span className="stat-label">Active users</span>
                </div>
              </div>

              {realtimeActions.length > 0 ? (
                <div className="realtime-feed">
                  {realtimeActions.map((action, index) => (
                    <div key={index} className="action-item">
                      <div className="action-icon">
                        {action.actionType.includes('product') && '🛍️'}
                        {action.actionType.includes('seller') && '👤'}
                        {action.actionType.includes('search') && '🔍'}
                        {action.actionType.includes('order') && '💰'}
                        {action.actionType.includes('review') && '⭐'}
                      </div>
                      <div className="action-details">
                        <strong>{action.actionType}</strong>
                        <p>
                          {action.targetResource?.name || action.actionDetails?.searchQuery || 'N/A'}
                        </p>
                        <small>
                          {action.userType} • {action.sessionContext?.deviceType} • 
                          {new Date(action.timing?.timestamp).toLocaleTimeString()}
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No recent activity in the last hour</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UserEngagement;
